/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Grid, Stack } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo, useState } from "react";
import {
  ProductGroupName,
  ProductionType,
  TimeAggregation,
} from "src/store/api/generatedApi";
import { SelectBox } from "../Selectbox";
import { TableHeader } from "../TableHeader";
import {
  GroupedProductionSeries,
  ProductionComparison,
  ProductionDenominator,
  ProductionSource,
} from "../types";
import { DateRangeSelector } from "components/common/DateRangeSelector";

import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useTenant } from "hooks/settings";
import { ToggleSelector } from "../ToggleSelector";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { ProductionPlot } from "./ProductionPlot";
import { ProductionTable } from "./ProductionTable";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { useDateRange } from "contexts/DateRangeContext";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";

interface ProductionTabProps {
  groupedProductionSeries: GroupedProductionSeries;
}

export const ProductionTab = ({
  groupedProductionSeries,
}: ProductionTabProps) => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();

  const [selectedTimeAggregation, setSelectedTimeAggregation] =
    useStringQueryParamState(
      "timeAggregation",
      getDefaultTimeAggregation(
        groupedProductionSeries.availableTimeAggregations
      )
    );

  const [selectedProductionComparison, setSelectedProductionComparison] =
    useStringQueryParamState<ProductionComparison>(
      "productionComparison",
      "sources"
    );

  const [selectedProductionType, setSelectedProductionType] =
    useStringQueryParamState<ProductionType | null>(
      "productionType",
      groupedProductionSeries.availableProductionTypes[0] ?? null
    );

  const [selectedProductionDenominator, setSelectedProductionDenominator] =
    useStringQueryParamState<ProductionDenominator>(
      "productionDenominator",
      "total"
    );

  const [selectedProductionSource, setSelectedProductionSource] =
    useStringQueryParamState(
      "productionSource",
      groupedProductionSeries.availableProductionSources[0] ?? null
    );

  const [selectedProductionSources, setSelectedProductionSources] =
    useLiteralListQueryParamState<ProductionSource>({
      paramName: "productionSources",
      permitted: groupedProductionSeries.availableProductionSources,
      localStorageKey: `${tenant}.strategyDashboard.production.selectedProductionSources`,
      defaultValue: groupedProductionSeries.availableProductionSources,
    });

  const [selectedProductGroups, setSelectedProductGroups] =
    useLiteralListQueryParamState<ProductGroupName>({
      paramName: "productionProductGroups",
      permitted: groupedProductionSeries.availableProductGroups,
      localStorageKey: `${tenant}.strategyDashboard.production.selectedProductGroups`,
      defaultValue: groupedProductionSeries.availableProductGroups,
    });

  const [selectedDate, setSelectedDate] = useState(() =>
    getDefaultSelectedDate(
      groupedProductionSeries,
      selectedTimeAggregation,
      selectedProductionType
    )
  );

  const availableTimeAggregationLabels = useMemo(
    () =>
      typeSafeObjectFromEntries(
        Array.from(groupedProductionSeries.availableTimeAggregations).map(
          (aggregation) => [aggregation, aggregation]
        )
      ),
    [groupedProductionSeries]
  );
  const availableProductionComparisons: Record<ProductionComparison, string> = {
    sources: "sources",
    productGroups: "productGroups",
  };
  const availableProductionDenominators: Record<ProductionDenominator, string> =
    {
      total: "total",
      perHeat: "perHeat",
    };
  const availableProductionTypes = useMemo(
    () =>
      typeSafeObjectFromEntries(
        Array.from(groupedProductionSeries.availableProductionTypes).map(
          (productionType) => [productionType, `${productionType}Mass`]
        )
      ),
    [groupedProductionSeries]
  );

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <DateRangeSelector />
        <ProductionExcelButton />
      </Stack>
      <Stack direction="row" spacing={2} sx={{ alignItems: "flex-end", mt: 1 }}>
        {setSelectedTimeAggregation === null ? null : (
          <ToggleSelector<TimeAggregation>
            title={"frequency"}
            selectedValue={selectedTimeAggregation}
            setSelectedValue={setSelectedTimeAggregation}
            availableValueLabels={availableTimeAggregationLabels}
          />
        )}
        <ToggleSelector<ProductionComparison>
          title={"comparison"}
          selectedValue={selectedProductionComparison}
          setSelectedValue={setSelectedProductionComparison}
          availableValueLabels={availableProductionComparisons}
        />
        {selectedProductionType === null ? null : (
          <ToggleSelector<ProductionType>
            title={"productionType"}
            selectedValue={selectedProductionType}
            setSelectedValue={setSelectedProductionType}
            availableValueLabels={availableProductionTypes}
          />
        )}
        <ToggleSelector<ProductionDenominator>
          title={"show"}
          selectedValue={selectedProductionDenominator}
          setSelectedValue={setSelectedProductionDenominator}
          availableValueLabels={availableProductionDenominators}
        />
        {selectedProductionComparison === "productGroups" ? (
          <SelectBox<ProductionSource>
            availableOptions={
              groupedProductionSeries.availableProductionSources
            }
            selectedOption={selectedProductionSource}
            onOptionChange={setSelectedProductionSource}
            id="production-source-select"
            labelId="production-source-label"
            label={t("source")}
          />
        ) : null}
        {selectedProductionComparison === "sources" ? (
          <MultiSelect<ProductionSource, ProductionSource>
            options={Array.from(
              groupedProductionSeries.availableProductionSources
            )}
            getOptionId={(source) => source}
            getOptionLabel={(source) => t(source)}
            values={selectedProductionSources}
            setValues={setSelectedProductionSources}
            small
            selectAll
            labelAbove
            label={t("sources")}
          />
        ) : (
          <MultiSelect<ProductGroupName, ProductGroupName>
            options={groupedProductionSeries.availableProductGroups}
            getOptionId={(productGroup) => productGroup}
            getOptionLabel={(productGroup) => t(productGroup)}
            values={selectedProductGroups}
            setValues={setSelectedProductGroups}
            small
            selectAll
            labelAbove
            label={t("productGroups")}
          />
        )}
      </Stack>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 8 }} ml={0}>
          {selectedProductionType === null ||
          selectedTimeAggregation === null ? null : (
            <ProductionPlot
              productionSeries={groupedProductionSeries}
              selectedTimeAggregation={selectedTimeAggregation}
              selectedProductionComparison={selectedProductionComparison}
              selectedProductionType={selectedProductionType}
              selectedProductionDenominator={selectedProductionDenominator}
              selectedProductionSource={selectedProductionSource}
              selectedProductionSources={selectedProductionSources}
              selectedProductGroups={selectedProductGroups}
              setSelectedDate={setSelectedDate}
            />
          )}
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TableHeader
            titlePrefix={t("productionDuring")}
            subtitleText={t("productionTableSubtitleText")}
            selectedDate={selectedDate}
            formatDateMonthly={selectedTimeAggregation === "monthly"}
          />
          {selectedProductionType === null ||
          selectedTimeAggregation === null ? null : (
            <ProductionTable
              productionSeries={groupedProductionSeries}
              selectedDate={selectedDate}
              selectedTimeAggregation={selectedTimeAggregation}
              selectedProductionType={selectedProductionType}
              selectedProductionDenominator={selectedProductionDenominator}
              selectedProductionSources={selectedProductionSources}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

const getDefaultSelectedDate = (
  productionSeries: GroupedProductionSeries,
  selectedTimeAggregation: TimeAggregation | null,
  selectedProductionType: ProductionType | null
): Date | null => {
  const defaultSelectedDateString = productionSeries.items
    .filter(
      (item) =>
        item[0].timeAggregation == selectedTimeAggregation &&
        item[0].productionType == selectedProductionType
    )
    .map((item) => item[1].date[item[1].date.length - 1] ?? null)
    .reduce((max, current) => {
      if (max === null) {
        return current;
      }
      if (current === null) {
        return max;
      }
      return current > max ? current : max;
    }, null);
  return defaultSelectedDateString
    ? new Date(Date.parse(defaultSelectedDateString))
    : null;
};

const getDefaultTimeAggregation = (
  availableTimeAggregations: Set<TimeAggregation>
) => {
  return availableTimeAggregations.has("daily")
    ? "daily"
    : (Array.from(availableTimeAggregations)[0] ?? null);
};

const ProductionExcelButton = () => {
  const { startDate, endDate } = useDateRange();
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardProduction.useLazyQuery();
  const start = startDate.format();
  const end = endDate.format();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start,
      end,
      "Cache-Control": "no-cache",
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};
